import { z } from 'zod';

export const truck_attributes_defaults = {
    width_m: 0,
    height_m: 0,
    length_m: 0,
    weight_t: 0,
    load_t: 0,
};

export const open_hours_defaults = {
    monday: { open: '', close: '' },
    tuesday: { open: '', close: '' },
    wednesday: { open: '', close: '' },
    thursday: { open: '', close: '' },
    friday: { open: '', close: '' },
    saturday: { open: '', close: '' },
    sunday: { open: '', close: '' },
};

export const vehicleDefaults: VehicleRequestType = {
    // id: null,
    email: null,
    webuser_id: null,
    name: '',
    enabled: true,
    route_optimize: true,
    route_end: null,
    truck_attributes: truck_attributes_defaults,
    open_hours: open_hours_defaults,
    weight_limit: null,
    parcel_limit: null,
    warehouse_id: null,
    track: true,
    last_position: null,
};

export const TruckAttributesSchema = z
    .object({
        width_m: z.coerce.number().min(0).catch(truck_attributes_defaults.width_m),
        height_m: z.coerce.number().min(0).catch(truck_attributes_defaults.height_m),
        length_m: z.coerce.number().min(0).catch(truck_attributes_defaults.length_m),
        weight_t: z.coerce.number().min(0).catch(truck_attributes_defaults.weight_t),
        load_t: z.coerce.number().min(0).catch(truck_attributes_defaults.load_t),
    })
    .catch(truck_attributes_defaults);
export type TruckAttributesType = z.infer<typeof TruckAttributesSchema>;

export const OpenHoursSchema = z
    .record(
        z.string(), // monday, tuesday, wednesday, thursday, friday, saturday, sunday
        z.object({
            open: z.union([z.literal(false), z.string().regex(/^(\d{2}:\d{2})?$/)]).default(''),
            close: z.union([z.literal(false), z.string().regex(/^(\d{2}:\d{2})?$/)]).default(''),
        })
    )
    .catch(open_hours_defaults);
export type OpenHoursType = z.infer<typeof OpenHoursSchema>;

export const RouteEndSchema = z
    .object({
        query: z.string().catch(''),
        coordinates: z
            .object({
                x: z.coerce.number(),
                y: z.coerce.number(),
            })
            .nullable(),
    })
    .nullable();
export type RouteEndType = z.infer<typeof RouteEndSchema>;

export const VehicleLimitSchema = z.object({
    weight_limit: z.number().nullable().catch(null),
    parcel_limit: z.number().nullable().catch(null),
});
export type VehicleLimitType = z.infer<typeof VehicleLimitSchema>;

export const VehicleLastPositionSchema = z.object({
    // vehicle_id: z.number().optional(),
    x: z.coerce.number(),
    y: z.coerce.number(),
    ts: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/),
    parking: z.boolean().catch(false),
    details: z.any().catch({ nextJobPoint: z.any() }),
    jobs: z.array(z.number()).catch([]),
});
export type VehicleLastPositionType = z.infer<typeof VehicleLastPositionSchema>;

/*
{
  "180" : {
    "x" : "20.69424",
    "y" : "52.19536",
    "ts" : "2024-04-24 16:00:33"
  }
}
 */
export const VehicleLastPositionResponseSchema = z
    .record(z.coerce.number(), VehicleLastPositionSchema)
    .catch({});
export type VehicleLastPositionResponseType = z.infer<typeof VehicleLastPositionResponseSchema>;

export const VehicleRequestSchema = VehicleLimitSchema.merge(
    z.object({
        email: z.string().nullable().catch(null),
        name: z.string().catch(''),
        warehouse_id: z.number().nullable().catch(null),
        webuser_id: z.number().nullable().catch(null),
        route_optimize: z.boolean().catch(true),
        route_end: RouteEndSchema,
        truck_attributes: TruckAttributesSchema,
        open_hours: OpenHoursSchema.nullable().catch(null),
        enabled: z.boolean().catch(true).describe('Vehicle can be tracked'),
        track: z.boolean().catch(true).describe('Track vehicle position (last_position)'),
        active: z.boolean().optional().describe('Vehicle with email confirmed'),
        last_position: VehicleLastPositionSchema.pick({ x: true, y: true, ts: true })
            .nullable()
            .catch(null),
        ranking_percent: z.number().nullable().catch(90),
        open_hours_adjust_percent: z.number().catch(0),
        color: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .nullable()
            .catch(null),
        // last_position_geom: VehicleLastPositionGeomSchema
    })
);
export type VehicleRequestType = z.infer<typeof VehicleRequestSchema>;

export const VehicleResponseSchema = VehicleRequestSchema.merge(
    z.object({
        id: z.coerce.number().nullable().catch(null),
        // created: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/),
        // updated: z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/),
        // creator_id: z.number(),
    })
);
export type VehicleResponseType = z.infer<typeof VehicleResponseSchema>;

export type VehicleCallbacks = {
    onSaveAll(vehicles: VehicleResponseType[]): boolean;
    onSaveOne(vehicle: VehicleResponseType): boolean;
};

export const CountSchema = z.object({
    count: z.coerce.number().catch(0),
});
export type CountType = z.infer<typeof CountSchema>;
